'use client';
import { jsxs, jsx } from "react/jsx-runtime";
import { Icon } from "@tameeni/icons";
import { cn } from "@tameeni/shared-logic";
import React__default, { useState, useEffect } from "react";
import ReactSelect, { components } from "react-select";
import { Button } from "../button/button.js";
import { getClassNames } from "./components.js";
import FloatingPlaceholder from "./floating-placeholder.js";
const CustomMenu = (props) => {
  const onWheel = (e) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const atTop = scrollTop === 0;
    const atBottom = scrollTop + clientHeight === scrollHeight;
    if (e.deltaY < 0 && atTop || e.deltaY > 0 && atBottom) {
      return;
    }
    e.stopPropagation();
  };
  return /* @__PURE__ */ jsx(
    components.Menu,
    {
      ...props,
      innerProps: {
        ...props.innerProps,
        onWheel
      }
    }
  );
};
const CustomMenuList = (props) => {
  const onWheel = (e) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const atTop = scrollTop === 0;
    const atBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
    if (e.deltaY < 0 && atTop || e.deltaY > 0 && atBottom) {
      return;
    }
    e.stopPropagation();
  };
  return /* @__PURE__ */ jsx(
    components.MenuList,
    {
      ...props,
      innerProps: {
        ...props.innerProps,
        onWheel
      }
    }
  );
};
const SingleSelect = React__default.forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props, ref) => {
    const {
      value,
      options,
      className,
      onChange,
      onClear,
      placeholder,
      isInvalid = false,
      isDisabled,
      isLoading,
      isSearchable,
      styles,
      components: components2,
      floatingPlaceholderClassName,
      // Extract our custom props
      ...restSelectProps
      // All remaining props will be passed to ReactSelect
    } = props;
    const [isSelected, setIsSelected] = useState(!!value);
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const selectedValue = React__default.useMemo(() => {
      return options.find((item) => String(item.value) === String(value));
    }, [value, options]);
    useEffect(() => {
      setIsSelected(!!selectedValue);
    }, [selectedValue]);
    const ClearIndicator = (props2) => {
      const {
        getStyles,
        innerProps: { ref: ref2, ...restInnerProps }
      } = props2;
      return /* @__PURE__ */ jsx(
        "div",
        {
          ...restInnerProps,
          ref: ref2,
          style: getStyles("clearIndicator", props2),
          children: /* @__PURE__ */ jsx(
            Button,
            {
              variant: "text",
              colorPalette: "slate",
              "aria-label": "Clear",
              size: "loose",
              onClick: onClear,
              ...props2,
              children: /* @__PURE__ */ jsx(Icon, { name: "Close" })
            }
          )
        }
      );
    };
    return /* @__PURE__ */ jsxs("div", { className: cn("relative", className), children: [
      /* @__PURE__ */ jsx(
        ReactSelect,
        {
          options,
          unstyled: true,
          ref,
          value: selectedValue,
          noOptionsMessage: () => "No options",
          onMenuOpen: () => setIsMenuOpened(true),
          onMenuClose: () => setIsMenuOpened(false),
          components: {
            ClearIndicator,
            Menu: CustomMenu,
            MenuList: CustomMenuList,
            ...components2
          },
          onChange: (newValue, meta) => {
            if (meta.action === "clear") {
              onClear == null ? void 0 : onClear();
              setIsSelected(false);
            } else if (newValue) {
              const value2 = newValue.value;
              onChange == null ? void 0 : onChange(String(value2));
              setIsSelected(true);
            }
          },
          classNames: getClassNames({
            isInvalid,
            isMenuOpened: isMenuOpened || isInputFocused,
            isSelected
          }),
          isDisabled,
          isLoading,
          isSearchable,
          menuPortalTarget: typeof window !== "undefined" ? document.body : void 0,
          styles: {
            ...styles,
            menuPortal: (base, _props) => ({
              ...base,
              pointerEvents: "auto",
              zIndex: 9999
            })
          },
          placeholder,
          ...restSelectProps
        },
        `react_select_key_${selectedValue == null ? void 0 : selectedValue.value}`
      ),
      /* @__PURE__ */ jsx(
        FloatingPlaceholder,
        {
          className: floatingPlaceholderClassName,
          isSelected,
          children: placeholder
        }
      )
    ] });
  }
);
SingleSelect.displayName = "SingleSelect";
export {
  SingleSelect
};
